import { useTenantStore } from '@/stores/tenantStore'
import { useSystemsList } from '@api/hooks/useCrossroads'
import { fetchClient } from '@api/typedClient'
import { CrossroadsMapping, MAPPING_ENTITY_MAP } from '@api/types'
import type { CreateMappingPrefillValues } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/useCreateMappingDrawer'
import { Button, Drawer, Form, Input, Select, Space, message } from 'antd'
import type React from 'react'
import { useEffect } from 'react'

interface CreateMappingDrawerProps {
  open: boolean
  onClose: () => void
  onCreate?: (mapping: CrossroadsMapping) => void
  prefillValues?: CreateMappingPrefillValues | null
}

/**
 * Create drawer component with auto-generated form fields
 */
export const CreateMappingDrawer: React.FC<CreateMappingDrawerProps> = ({
  open,
  onClose,
  onCreate,
  prefillValues,
}) => {
  const { selectedTenant } = useTenantStore()
  const [form] = Form.useForm()

  // Prefill form when drawer opens with prefill values
  useEffect(() => {
    if (open && prefillValues) {
      form.setFieldsValue(prefillValues)
    }
  }, [open, prefillValues, form])

  // Reset form when drawer closes
  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleSubmit = async () => {
    // Submission logic
    const formData = await form.validateFields()
    const mapping: CrossroadsMapping = {
      ...formData,
      goids: [],
      matching_info: {},
      extra_data: {},
      children: {},
    }

    const { data, error } = await fetchClient.POST('/crossroads/mappings/', {
      body: {
        goids: [],
        source_system: mapping.source_system,
        source_id: mapping.source_id,
        display_name: mapping.display_name,
        tenant: selectedTenant?.name || '',
        // @ts-expect-error TODO: Ben will fix the enum variants (location is not real anymore)
        details: { type: mapping.type },
      },
    })

    if (error) {
      console.error('Failed to create mapping:', error)
      return
    }

    handleClose()
    message.success('Mapping created successfully')

    onCreate?.(data as unknown as CrossroadsMapping)
  }

  const { data: sourceSystems, isLoading } = useSystemsList()

  const tenantSystemIds = new Set(
    selectedTenant?.instances?.map((i) => i.system_id),
  )
  const filteredSourceSystems = sourceSystems?.filter((sys) =>
    tenantSystemIds.has(sys._id || sys.id),
  )

  return (
    <Drawer
      title={`Create New Mapping`}
      open={open}
      onClose={handleClose}
      width={520}
      destroyOnHidden={false}
      footer={
        <Space style={{ float: 'left' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type='primary' onClick={handleSubmit}>
            Create
          </Button>
        </Space>
      }
    >
      <Form form={form} layout='vertical' autoComplete='off'>
        <Form.Item
          key='type'
          name='type'
          label='Type'
          rules={[{ required: true }]}
        >
          <Select>
            {Object.keys(MAPPING_ENTITY_MAP)
              .filter((type) => type !== 'tank')
              .map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          key='display_name'
          name='display_name'
          label='Display Name'
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          key='source_id'
          name='source_id'
          label='Source ID'
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          key='source_system'
          name='source_system'
          label='Source System'
          rules={[{ required: true }]}
        >
          <Select loading={isLoading} disabled={isLoading}>
            {!isLoading &&
              filteredSourceSystems?.map((sys) => (
                <Select.Option key={sys.name} value={sys.name}>
                  {sys.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Form>
    </Drawer>
  )
}
